import {
  Box,
  Button,
  chakra,
  Flex,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast
} from "@chakra-ui/react";
import { FormSelect, FormTextArea } from "../../../components/Forms";
import * as React from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetAllSuppliers } from "../../../hooks/query";
import {
  constructQueryParamsString,
  showErrorToast,
  sortAlphabeticallyByAttribute,
  USER_ROLES,
  USER_TYPES
} from "../../../utils";
import { hideAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useGetUsers, useGetUsersRoles } from "../../../hooks/query/user-administration";
import { SUPPLIER_NOTES_STATUS } from "../SupplierNotes";
import { sortUsersByEmail } from "../../../utils/userSort";

function SupplierNotesModal({ modalData, isOpen, onClose, onSubmit }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierNotes.modal"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();
  const [suppliers, setSuppliers] = useState([]);
  const [contactPersonsList, setContactPersonsList] = useState([]);
  const [status, setStatus] = useState(SUPPLIER_NOTES_STATUS.OPEN);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: modalData
  });

  useEffect(() => {
    if (modalData && typeof modalData.status !== "undefined") {
      setStatus(modalData.status);
    }
  }, [modalData]);

  const prepareSubmitData = data => {
    onSubmit(modalData?.id, {
      supplier: data.supplier,
      contactPerson: data.contactPerson,
      internalNote: data.internalNote,
      supplierNote: data.supplierNote,
      status: status
    });
  };

  const {
    isFetching: isFetchingSuppliers
  } = useGetAllSuppliers({
    onSuccess: (data) => {
      setSuppliers(data.sort((a, b) => sortAlphabeticallyByAttribute(a, b, "name")));
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const {
    data: orgMemberRoles
  } = useGetUsersRoles({
    userType: USER_TYPES.ORG_MEMBER
  }, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message)
  });

  const getAdminUsersQueryParamsString = () => {
    return constructQueryParamsString(
      {
        page: 0,
        size: 1000,
        roleIds: [
          { value: orgMemberRoles?.roles?.find(roleObj => roleObj.role === USER_ROLES.ORG_ADMIN)?.id ?? -1 },
          { value: orgMemberRoles?.roles?.find(roleObj => roleObj.role === USER_ROLES.SUPPLIER_ADMIN)?.id ?? -1 }
        ]
      },
      [{ defaultAttribute: "roleIds", multipleValuesAttribute: "roleIds" }]
    );
  };

  const {
    isFetching: isFetchingUsers
  } = useGetUsers(getAdminUsersQueryParamsString(),
    {
      onSuccess: (data) => {
        const users = sortUsersByEmail(data.page?.content?.map((user) => {
          return {
            id: user?.id,
            email: user?.email
          };
        }));
        setContactPersonsList(users);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      enabled: orgMemberRoles !== null && typeof orgMemberRoles !== "undefined"
    }
  );

  return isOpen && !isFetchingSuppliers && !isFetchingUsers ?
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
          <ModalHeader>{modalData ? t("updateSupplierNotesHeader") : t("createSupplierNotesHeader")}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Flex direction="column" gap="2">
              <Text fontSize="sm">{t("description")}</Text>
              <FormSelect
                label={`${t("labels.supplier")} *`}
                placeholder={" "}
                isDisabled={!!modalData?.id}
                errors={errors}
                {...register("supplier", {
                  required: globalTranslation("entityRequired", { entityName: t("labels.supplier") })
                })}
                maxInlineSize={{ base: "full", lg: "lg" }}
              >
                {suppliers.map(supplier =>
                  <option key={`supplier-option-${supplier.id}`} value={supplier.name}>
                    {supplier.name}
                  </option>
                )}
              </FormSelect>
              <Box>
                <FormLabel
                  opacity="0.75"
                  fontSize="xs"
                  whiteSpace="nowrap"
                  marginBlockEnd="0"
                  blockSize="5"
                  isTruncated
                  color="esg_gray.200"
                >
                  {t("labels.status")}
                </FormLabel>
                <RadioGroup
                  id={"supplierNoteStatusRadioGroup"}
                  value={status}
                  style={{ position: "relative" }}
                  minHeight={"40px"}
                  display={"flex"}
                  alignItems={"center"}
                >
                  <Stack spacing={5} direction="row">
                    <Radio
                      colorScheme="customScheme"
                      value={SUPPLIER_NOTES_STATUS.OPEN}
                      onChange={e => {
                        setStatus(e?.target.value);
                      }}
                    >
                      {t("buttons.open")}
                    </Radio>
                    <Radio
                      colorScheme="customScheme"
                      value={SUPPLIER_NOTES_STATUS.COMPLETED}
                      onChange={e => {
                        setStatus(e?.target.value);
                      }}
                    >
                      {t("buttons.completed")}
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>
              <FormTextArea
                label={
                  <Flex gap={2}>
                    <Text>
                      {t("labels.internalNote")}
                    </Text>
                    <Text fontWeight="bold" fontStyle="italic">
                      [{t("labels.internalNoteDesc")}]
                    </Text>
                  </Flex>
                }
                errors={errors}
                {...register("internalNote")}
                maxHeight="200"
                minHeight="200"
              />
              <FormSelect
                label={t("labels.contactPerson")}
                placeholder={" "}
                errors={errors}
                {...register("contactPerson")}
                maxInlineSize={{ base: "full", lg: "lg" }}
              >
                {contactPersonsList.map(person =>
                  <option key={`person-option-${person.email}`} value={person.email}>
                    {person.email}
                  </option>
                )}
              </FormSelect>
              <FormTextArea
                label={
                  <Flex gap={2}>
                    <Text>
                      {t("labels.supplierNote")}
                    </Text>
                    <Text fontWeight="bold" fontStyle="italic">
                      [{t("labels.supplierNoteDesc")}]
                    </Text>
                  </Flex>
                }
                errors={errors}
                {...register("supplierNote")}
                maxHeight="200"
                minHeight="200"
              />
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t("buttons.close")}
            </Button>
            <Button colorScheme="customScheme" type="submit">
              {t("buttons.save")}
            </Button>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal> : null;
}

export default SupplierNotesModal;